<template>
  <Card v-if="shouldRenderCard" class="mt-4">
    <template #title>
      <div class="flex items-center justify-between">
        <span class="flex items-center gap-2">
          <i class="pi pi-users"></i>
          <span>العلاقات</span>
          <Tag v-if="relationships.length" :value="relationships.length" severity="info" />
        </span>
        <Button
          v-if="!readonly"
          icon="pi pi-refresh"
          label="تحديث"
          size="small"
          text
          @click="loadRelationships"
          :disabled="loading"
        />
      </div>
    </template>

    <template #content>
      <div v-if="!hasEmployeeId" class="text-color-secondary text-sm">
        لا يمكن إضافة العلاقات قبل حفظ الموظف.
      </div>

      <div v-else>
        <div v-if="loading" class="py-3 text-color-secondary text-sm">
          جاري تحميل العلاقات...
        </div>

        <div v-if="!readonly" class="relationship-form mb-4">
          <h4 class="text-base font-semibold mb-3">
            {{ editingId ? "تعديل علاقة" : "إضافة علاقة جديدة" }}
          </h4>
          <div class="grid gap-3 md:grid-cols-2">
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium">نوع العلاقة</label>
              <Dropdown
                v-model="form.relationship_type_id"
                :options="relationshipTypes"
                optionLabel="label"
                optionValue="relationship_type_id"
                placeholder="اختر نوع العلاقة"
                :disabled="loadingTypes"
                class="w-full"
              />
              <small v-if="formErrors.type" class="p-error">{{ formErrors.type }}</small>
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium">الموظف المرتبط</label>
              <AutoComplete
                v-model="form.relatedEmployeeOption"
                :suggestions="employeeSuggestions"
                :completeOnFocus="false"
                :delay="250"
                :forceSelection="true"
                optionLabel="label"
                :disabled="editingDirection === 'incoming'"
                placeholder="اكتب اسم الموظف"
                @complete="searchEmployees"
                class="w-full"
              />
              <small v-if="editingDirection === 'incoming'" class="text-color-secondary">
                لا يمكنك تغيير الموظف المرتبط لعلاقة قام بتعريفها موظف آخر.
              </small>
              <small v-if="formErrors.related" class="p-error">{{ formErrors.related }}</small>
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium">سياق / وصف مختصر</label>
              <InputText v-model.trim="form.context" placeholder="مثال: مشروع أبولو" />
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium">المصدر</label>
              <InputText v-model.trim="form.source" placeholder="LinkedIn, HR, ..." />
            </div>

            <div class="flex flex-col gap-1 md:col-span-2">
              <label class="text-sm font-medium">ملاحظات</label>
              <Textarea v-model.trim="form.notes" rows="2" autoResize />
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium">منذ</label>
              <InputText type="date" v-model="form.since" />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium">إلى</label>
              <InputText type="date" v-model="form.until" />
            </div>
          </div>
          <div class="flex gap-2 mt-3">
            <Button
              label="حفظ"
              icon="pi pi-save"
              :loading="saving"
              :disabled="!canSubmit || saving"
              @click="submitForm"
            />
            <Button
              v-if="editingId"
              label="إلغاء"
              icon="pi pi-times"
              severity="secondary"
              outlined
              @click="resetForm"
            />
          </div>
        </div>

        <Divider />

        <section class="mb-4">
          <h4 class="text-base font-semibold mb-2">العلاقات</h4>
          <p v-if="!allRelationships.length" class="text-color-secondary text-sm">
            لا توجد علاقات محددة بعد.
          </p>
          <ul class="list-none p-0 m-0 flex flex-col gap-3">
            <li
              v-for="rel in allRelationships"
              :key="rel.relationship_id"
              class="border rounded-lg p-3"
              :class="{ 'bg-surface-50': rel.direction === 'incoming' }"
            >
              <div class="flex justify-between items-start gap-3">
                <div class="flex flex-col gap-1">
                  <div class="flex items-center gap-2 flex-wrap">
                    <Tag
                      :severity="rel.direction === 'incoming' ? 'warning' : 'info'"
                      :value="rel.effective_relationship_type?.label || rel.relationship_type?.label"
                    />
                    <RouterLink
                      :to="relatedEmployeePath(rel.related_employee)"
                      class="font-semibold text-primary hover:underline inline-flex items-center gap-1"
                    >
                      {{ displayEmployeeName(rel.related_employee) }}
                      <i class="pi pi-external-link text-xs"></i>
                    </RouterLink>
                    <small v-if="rel.context" class="text-color-secondary">({{ rel.context }})</small>
                  </div>
                  <p v-if="rel.notes" class="text-sm text-color-secondary whitespace-pre-line">
                    {{ rel.notes }}
                  </p>
                  <small v-if="relDates(rel)" class="text-xs text-color-secondary">{{ relDates(rel) }}</small>
                </div>
                <div class="flex gap-2" v-if="!readonly">
                  <Button
                    icon="pi pi-pencil"
                    severity="secondary"
                    rounded
                    size="small"
                    @click="startEdit(rel)"
                  />
                  <Button
                    icon="pi pi-trash"
                    severity="danger"
                    rounded
                    size="small"
                    @click="deleteRelationship(rel.relationship_id)"
                  />
                </div>
              </div>
            </li>
          </ul>
        </section>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import axios from 'axios'
import Card from 'primevue/card'
import Button from 'primevue/button'
import Dropdown from 'primevue/dropdown'
import AutoComplete from 'primevue/autocomplete'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Divider from 'primevue/divider'
import Tag from 'primevue/tag'
import { useToast } from 'primevue/usetoast'

type RelationshipType = {
  relationship_type_id: number
  code: string
  label: string
  description?: string | null
  category?: string | null
  bidirectional: boolean
}

type EmployeeOption = {
  employee_id: number
  first_name?: string | null
  last_name?: string | null
  company?: string | null
  department?: string | null
  location?: string | null
  label?: string
}

type RelationshipRow = {
  relationship_id: number
  employee_id: number
  related_employee_id: number
  direction: 'outgoing' | 'incoming'
  context?: string | null
  notes?: string | null
  since?: string | null
  until?: string | null
  source?: string | null
  relationship_type?: RelationshipType | null
  effective_relationship_type?: RelationshipType | null
  related_employee?: EmployeeOption | null
}

const props = defineProps<{ employeeId: number | string | undefined, readonly?: boolean, hideWhenEmpty?: boolean }>()

const relationships = ref<RelationshipRow[]>([])
const relationshipTypes = ref<RelationshipType[]>([])
const employeeSuggestions = ref<EmployeeOption[]>([])
const loading = ref(false)
const loadingTypes = ref(false)
const saving = ref(false)
const editingId = ref<number | null>(null)
const editingDirection = ref<'incoming' | 'outgoing' | null>(null)
const toast = useToast()

const form = reactive({
  relationship_type_id: null as number | null,
  relatedEmployeeOption: null as EmployeeOption | null,
  context: '',
  notes: '',
  since: '',
  until: '',
  source: '',
})

const formErrors = reactive<{ type?: string; related?: string }>({})

const employeeNumber = computed(() => {
  const val = Number(props.employeeId)
  return Number.isFinite(val) ? val : null
})

const hasEmployeeId = computed(() => !!employeeNumber.value)

const allRelationships = computed(() =>
  [...relationships.value]
)

const shouldRenderCard = computed(() => {
  if (!props.hideWhenEmpty) return true
  if (!props.readonly) return true
  if (loading.value) return true
  return allRelationships.value.length > 0
})

const canSubmit = computed(
  () => !!form.relationship_type_id && !!form.relatedEmployeeOption?.employee_id
)

watch(
  () => props.employeeId,
  () => {
    resetForm()
    loadRelationships()
  }
)

onMounted(async () => {
  if (!props.readonly) {
    await loadRelationshipTypes()
  }
  await loadRelationships()
})

async function loadRelationshipTypes() {
  loadingTypes.value = true
  try {
    const { data } = await axios.get<RelationshipType[]>('/api/employees/relationship-types')
    relationshipTypes.value = data
  } finally {
    loadingTypes.value = false
  }
}

async function loadRelationships() {
  if (!hasEmployeeId.value) return
  loading.value = true
  try {
    const { data } = await axios.get<RelationshipRow[]>(
      `/api/employees/${employeeNumber.value}/relationships`
    )
    relationships.value = data
  } finally {
    loading.value = false
  }
}

async function searchEmployees(event: { query: string }) {
  const term = (event.query || '').trim()
  if (!term) {
    employeeSuggestions.value = []
    return
  }
  const { data } = await axios.get<EmployeeOption[]>('/api/employees/lookup', {
    params: { q: term, exclude_employee_id: employeeNumber.value },
  })
  employeeSuggestions.value = data.map((row) => ({
    ...row,
    label: row.label || formatLabelText(row),
  }))
}

function formatLabelText(emp?: EmployeeOption | null) {
  if (!emp) return ''
  if (emp.label) return emp.label
  const companyPart = emp.company ? `${emp.company}` : ''
  const parts = [
    `employee_id ${emp.employee_id}`,
    emp.first_name || '',
    emp.last_name || '',
    companyPart,
    emp.department || '',
    emp.location || '',
  ]
  return parts.filter(Boolean).join(' ').trim()
}

function displayEmployeeName(emp?: EmployeeOption | null) {
  if (!emp) return '—'
  const name = [emp.first_name, emp.last_name].filter(Boolean).join(' ').trim()
  return name || '—'
}

function relDates(rel: RelationshipRow) {
  if (!rel.since && !rel.until) return ''
  const since = rel.since ? new Date(rel.since).toLocaleDateString('en-CA') : 'غير محدد'
  const until = rel.until ? new Date(rel.until).toLocaleDateString('en-CA') : 'مستمر'
  return `${since} → ${until}`
}

function resetForm() {
  editingId.value = null
  editingDirection.value = null
  form.relationship_type_id = null
  form.relatedEmployeeOption = null
  form.context = ''
  form.notes = ''
  form.since = ''
  form.until = ''
  form.source = ''
  formErrors.type = undefined
  formErrors.related = undefined
}

function startEdit(rel: RelationshipRow) {
  editingId.value = rel.relationship_id
  editingDirection.value = rel.direction
  form.relationship_type_id =
    rel.relationship_type?.relationship_type_id || rel.effective_relationship_type?.relationship_type_id || null
  form.relatedEmployeeOption = rel.related_employee
    ? { ...rel.related_employee, label: formatLabelText(rel.related_employee) }
    : null
  form.context = rel.context || ''
  form.notes = rel.notes || ''
  form.since = rel.since || ''
  form.until = rel.until || ''
  form.source = rel.source || ''
}

function buildPayload() {
  return {
    relationship_type_id: form.relationship_type_id,
    related_employee_id: form.relatedEmployeeOption?.employee_id,
    context: form.context?.trim() || null,
    notes: form.notes?.trim() || null,
    since: form.since || null,
    until: form.until || null,
    source: form.source?.trim() || null,
  }
}

async function submitForm() {
  formErrors.type = form.relationship_type_id ? undefined : 'الرجاء اختيار نوع العلاقة'
  formErrors.related = form.relatedEmployeeOption ? undefined : 'الرجاء اختيار الموظف المرتبط'
  if (!canSubmit.value || !employeeNumber.value) return

  const payload = buildPayload()
  saving.value = true
  try {
    if (editingId.value) {
      await axios.put(
        `/api/employees/${employeeNumber.value}/relationships/${editingId.value}`,
        payload
      )
      toast.add({ severity: 'success', summary: 'تم التحديث', life: 2000 })
    } else {
      await axios.post(`/api/employees/${employeeNumber.value}/relationships`, payload)
      toast.add({ severity: 'success', summary: 'تمت الإضافة', life: 2000 })
    }
    await loadRelationships()
    resetForm()
  } catch (err: any) {
    const detail = err?.response?.data?.detail || 'فشل حفظ العلاقة'
    toast.add({ severity: 'error', summary: 'خطأ', detail, life: 4000 })
  } finally {
    saving.value = false
  }
}

async function deleteRelationship(relationshipId: number) {
  if (!employeeNumber.value) return
  try {
    await axios.delete(`/api/employees/${employeeNumber.value}/relationships/${relationshipId}`)
    toast.add({ severity: 'success', summary: 'تم الحذف', life: 2000 })
    if (editingId.value === relationshipId) {
      resetForm()
    }
    await loadRelationships()
  } catch (err: any) {
    const detail = err?.response?.data?.detail || 'تعذر حذف العلاقة'
    toast.add({ severity: 'error', summary: 'خطأ', detail, life: 4000 })
  }
}

function relatedEmployeePath(emp?: EmployeeOption | null) {
  if (!emp?.employee_id) return '#'
  return `/employee/${emp.employee_id}`
}
</script>

<style scoped>
.relationship-form {
  border: 1px dashed var(--p-primary-color, #0ea5e9);
  border-radius: 0.75rem;
  padding: 1rem;
  background-color: rgba(14, 165, 233, 0.04);
}

</style>
